import { useEffect, useState } from 'react';
import { getPageZoom } from '../utils/desktopZoomFix';

// A tab of the left bar (the tabs themselves, not the rows inside their menus).
const TAB = '.v3-actions > button, .v3-actions > a, .v3-actions > * > button:first-child, .v3-actions > * > a:first-child';
// Slide-out menus are drawn inside the bar's markup but are not the bar: no tips inside them.
const MENU = '[style*="z-index: 9999"], [data-testid="log-call-dropdown-menu"]';

/**
 * The hover label for both edge bars, in the V3 look and with no delay (the browser's own grey tip waits half a second and ignores the theme).
 * It shows for anything in the bar that carries a title or a data-v3-tip, and for the tabs of the minimised left bar (which are icons only, so
 * their name is read from the tab). A title is taken over the moment the pointer arrives, so the browser's own tip never shows as well.
 * `side` is the side the label opens to: 'right' for the left bar, 'left' for the right bar.
 */
const V3BarTip = ({ barRef, side }) => {
  const [tip, setTip] = useState(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const hide = () => setTip(null);
    const onOver = (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      if (!origin || origin.closest(MENU)) { hide(); return; }
      let element = origin.closest('[data-v3-tip], [title]');
      let text = '';
      if (element && bar.contains(element)) {
        text = element.getAttribute('data-v3-tip') || element.getAttribute('title') || '';
        if (element.hasAttribute('title')) {
          element.setAttribute('data-v3-tip', element.getAttribute('title'));
          element.removeAttribute('title');
        }
      } else {
        element = null;
        if (bar.classList.contains('v3-bar--strip')) {
          const tab = origin.closest(TAB);
          if (tab && bar.contains(tab)) { element = tab; text = (tab.textContent || '').trim(); }
        }
      }
      if (!element || !text) { hide(); return; }
      const rect = element.getBoundingClientRect();
      const zoom = getPageZoom() || 1;
      setTip({ text, x: (side === 'right' ? rect.right : rect.left) / zoom, y: (rect.top + rect.height / 2) / zoom });
    };
    bar.addEventListener('mouseover', onOver);
    bar.addEventListener('mouseleave', hide);
    bar.addEventListener('pointerdown', hide);
    return () => {
      bar.removeEventListener('mouseover', onOver);
      bar.removeEventListener('mouseleave', hide);
      bar.removeEventListener('pointerdown', hide);
    };
  }, [barRef, side]);

  if (!tip) return null;
  return (
    <div className={`v3-tip v3-tip--${side}`} style={{ left: tip.x, top: tip.y }} role="tooltip" data-testid="v3-bar-tip">{tip.text}</div>
  );
};

export default V3BarTip;
